/**
 * Service layer — Drawing Sheets domain
 *
 * Owns: Drawing Sheets (tblXFwxWKECjoVj9O),
 *       Sheet Revisions (tblBfKyVWRAlW3tmj),
 *       Drawing Sheet Templates (tblNWHEYC83Qcc2zX)
 */

import { apiGet, apiGetOne, apiGetOneStrict, apiPatch, apiPost, apiPostBatch, apiDelete } from './_api.js'
import { getOfficialPdf } from './qcVersions.js'
import { resetSheetChecklist } from './checklist.js'

const TBL_SHEET = 'tblXFwxWKECjoVj9O'
const TBL_REV   = 'tblBfKyVWRAlW3tmj'
const TBL_TPL   = 'tblNWHEYC83Qcc2zX'

/**
 * The canonical PDF for a drawing sheet: the end-of-QC-rounds approved drawing (source of truth =
 * Supabase) for the caller's CURRENT revision (Posted-Date-resolved). Falls back to the provided
 * latest revision URL when that revision has no QC-approved version yet.
 * @param {string} drawingSheetId — Airtable Drawing Sheet record ID
 * @param {string|null} fallbackUrl — latest revision PDF to use if not yet approved
 * @param {string|null} revisionId — the Sheet Revision id the caller has already resolved as
 *   current (via Posted Date) — always pass this when known. Scopes the QC lookup to that one
 *   revision's own approval so an unrelated older revision's QC round can never outrank it — see
 *   getOfficialPdf's JSDoc for why this matters (2026-07-22 stale-export incident).
 * @returns {Promise<{ url: string|null, isQcApproved: boolean, approvedAt?: string }>}
 */
export async function getOfficialSheetPdf(drawingSheetId, fallbackUrl = null, revisionId = null) {
  try {
    const r = await getOfficialPdf(drawingSheetId, revisionId)
    if (r && r.url) return { url: r.url, isQcApproved: true, approvedAt: r.approvedAt }
  } catch { /* fall through to fallback */ }
  return { url: fallbackUrl || null, isQcApproved: false }
}

/**
 * Fetch a Sheet Revision record fresh and return its current PDF attachment URL.
 * Airtable attachment URLs (v5.airtableusercontent.com) are time-limited signed
 * links that expire (HTTP 410), so the QC markup tool re-reads this at open time
 * to get a fresh URL rather than relying on a stored one.
 * @param {string} revisionId — Sheet Revision record ID
 * @returns {Promise<string|null>} fresh PDF URL, or null if none
 */
/** The project record id a Drawing Sheet belongs to (its `Project` link), or ''. */
export async function getSheetProjectId(sheetId) {
  const rec = await apiGetOne(TBL_SHEET, sheetId)
  const p = rec && rec.fields && rec.fields['Project']
  return (Array.isArray(p) ? p[0] : p) || ''
}

export async function getRevisionPdfUrl(revisionId) {
  if (!revisionId) return null
  const rec = await apiGetOne(TBL_REV, revisionId)
  const a = rec?.fields?.PDF
  return Array.isArray(a) && a.length ? (a[0]?.url || null) : null
}

/**
 * Fetch all Drawing Sheets for a project, sorted by Drawing Sort Order.
 * Returns raw Airtable records. Includes Sheet Number and Sheet Title as
 * separate editable fields in addition to the combined formula field.
 *
 * Sorted by "Drawing Sort Order", not "Sheet Sort Order" — the latter's formula has no branch
 * for the "Special General" discipline (FDSAP) and falls through to a bare "-", which sorts
 * before every real value. "Drawing Sort Order" is built from the template's own discipline-sort
 * field instead and doesn't have this gap (2026-07-23).
 */
export function getProjectSheets(projectId) {
  // No fields[] restriction — fetch all fields so lookup fields return correctly and
  // Slack Thread Timestamp is included automatically once that field exists in Airtable.
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID from Project},","))'
  return apiGet(
    TBL_SHEET,
    'filterByFormula=' + encodeURIComponent(formula)
    + '&sort%5B0%5D%5Bfield%5D=Drawing+Sort+Order&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=200'
  )
}

/**
 * SHEET LINK SYNC (AlderCAD): what Cortex currently says about each Drawing Sheet an AlderCAD
 * model is linked to — `{ number, name, discipline }`, or `null` when the sheet was DELETED.
 * Read off the project's sheet list first; any linked id not in that list is looked up on its
 * own, and only a real NOT_FOUND counts as deleted. An id that could not be checked is left out
 * of the map (the model then leaves that sheet alone).
 * @param {string} projectId
 * @param {string[]} linkedIds
 * @param {object[]} [projectSheets] — getProjectSheets' result, when the caller already has it
 * @returns {Promise<Map<string, {number:string,name:string,discipline?:string}|null>>}
 */
export async function getLinkedSheetStates(projectId, linkedIds, projectSheets) {
  const recs = projectSheets || await getProjectSheets(projectId)
  const byId = new Map((recs || []).map(r => [r.id, r]))
  const shape = (r) => {
    const d = r.fields?.Discipline
    return { number: r.fields?.['Sheet Number'] || '', name: r.fields?.['Sheet Title'] || '', discipline: (Array.isArray(d) ? d[0] : d) || undefined }
  }
  const out = new Map()
  for (const id of [...new Set(linkedIds)]) {
    const r = byId.get(id)
    if (r) { out.set(id, shape(r)); continue }
    try {
      const one = await apiGetOneStrict(TBL_SHEET, id)
      out.set(id, one ? shape(one) : null)
    } catch { /* unknown — leave it out */ }
  }
  return out
}

/**
 * Fetch all Sheet Revision records for a single Drawing Sheet, sorted most-recent first.
 * Returns full records (no fields[] filter — lookup fields return empty with fields[]).
 * @param {string} sheetId — Drawing Sheet record ID
 */
export function getSheetRevisions(sheetId) {
  const filter = 'FIND("' + sheetId + '",ARRAYJOIN({Record ID (from Drawing Sheet)},","))'
  return apiGet(
    TBL_REV,
    'filterByFormula=' + encodeURIComponent(filter)
    + '&sort%5B0%5D%5Bfield%5D=Posted+Date+(from+Deliverables)&sort%5B0%5D%5Bdirection%5D=desc'
    + '&maxRecords=50'
  )
}

// Milestone deliverable types whose carried-forward sheets should default to that milestone's own
// revision kind instead of the generic 'Phase Advancement' — Matt (2026-07-23): a sheet pulled
// into e.g. DAPC Drawings Revisions IS a Plan Check Revision, not a generic advancement; the
// default should say so immediately instead of needing a manual re-label on every sheet.
export const CARRY_FORWARD_TYPE_BY_MS_TYPE = {
  'DAPC Drawings Revisions':          'Plan Check Revision',
  'PPC Drawings Plan Check Revision': 'Plan Check Revision',
  'PPC Drawings Permit Revision':     'Permit Revision',
  // Matt (2026-10-21): an ASI often revises sheets during construction — a sheet pulled into the
  // ASI milestone is an ASI Revision, so it reads as one in the sheet's own list of versions.
  'Architects Supplemental Instructions': 'ASI Revision',
}

/**
 * Add an existing Drawing Sheet to a milestone by creating a revision record. Type defaults to
 * 'Phase Advancement' unless the destination milestone is a revision-cycle type in
 * CARRY_FORWARD_TYPE_BY_MS_TYPE, in which case it defaults to that milestone's own revision kind.
 * @param {string} sheetId — Drawing Sheet record ID
 * @param {string} milestoneId — Milestone (Project Deliverable) record ID
 * @param {string} [milestoneType] — the destination milestone's own deliverable type name
 *   ("Name (from Deliverable)"), if the caller already has it (avoids an extra fetch here) —
 *   resolved from Airtable if omitted.
 * @param {string} [summary] — override for 'Summary of Change' (default is the carry-forward
 *   wording below) — e.g. the AlderCAD "send when ready" flow uses its own summary text.
 * @returns {Promise<object>} Created revision record
 */
export async function addExistingSheetToMilestone(sheetId, milestoneId, milestoneType, summary) {
  const [, priorRevs] = await Promise.all([
    assertSheetNotOnMilestone(sheetId, milestoneId),
    getSheetRevisions(sheetId),
  ])
  // A sheet that has never been on any milestone is being RELEASED here for the first time,
  // not carried forward — its first revision is the Original Release (2026-11-02; before this a
  // first send from AlderCAD recorded a Phase Advancement from a release that never happened).
  if (!priorRevs.length) {
    return apiPost(TBL_REV, {
      'Drawing Sheet':    [sheetId],
      'Deliverables':     [milestoneId],
      'Type':             'Original Release',
      'Summary of Change': summary || 'Initial release — sheet first issued in the drawing set.',
    })
  }
  let type = milestoneType
  if (type === undefined) {
    const ms = await apiGetOne('tblFuiDpahqCL4eIK', milestoneId)
    type = (ms?.fields?.['Name (from Deliverable)'] || [])[0] || ''
  }
  return apiPost(TBL_REV, {
    'Drawing Sheet':    [sheetId],
    'Deliverables':     [milestoneId],
    'Type':             CARRY_FORWARD_TYPE_BY_MS_TYPE[type] || 'Phase Advancement',
    'Summary of Change': summary || 'Sheet carried forward from a previous milestone.',
  }, { typecast: true }) // 'ASI Revision' is a newer Type choice; typecast creates it on first use
}

/**
 * Patch the editable fields of a Drawing Sheet record.
 * Note: 'Sheet Number and Title' is a formula — write to 'Sheet Number' and 'Sheet Title' separately.
 * @param {string} sheetId
 * @param {{ sheetNumber?: string, sheetTitle?: string }} fields
 */
export function updateSheet(sheetId, { sheetNumber, sheetTitle, templateId } = {}) {
  const fields = {}
  if (sheetNumber !== undefined) fields['Sheet Number'] = sheetNumber
  if (sheetTitle  !== undefined) fields['Sheet Title']  = sheetTitle
  if (templateId  !== undefined) fields['Drawing Sheet Template'] = templateId ? [templateId] : []
  return apiPatch(TBL_SHEET, sheetId, fields)
}

/**
 * Upload a PDF to Supabase Storage (the same bucket/credential path `uploadRevisionPdf` uses)
 * WITHOUT patching any Airtable record — just the storage half. For flows where a server-side
 * validation step must pass before any record should be touched (e.g. `overrideReplacePdf`'s
 * aspect-ratio guard) — patching Airtable first and having the validation fail afterward would
 * leave the revision pointing at a PDF no in-app record agrees with.
 * @param {File} file — the PDF File object
 * @returns {Promise<string>} The public URL of the uploaded PDF
 */
export async function uploadPdfToStorage(file) {
  const token = await window.__clerkGetToken()

  const credRes = await fetch('/api/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ action: 'revisionPdfUploadUrl', filename: file.name }),
  })
  if (!credRes.ok) {
    const b = await credRes.json().catch(() => ({}))
    throw new Error(b.error || ('Could not get upload URL (' + credRes.status + ')'))
  }
  const { signedUrl, publicUrl } = await credRes.json()

  const uploadRes = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/pdf', 'x-upsert': 'true' },
    body: file,
  })
  if (!uploadRes.ok) {
    const detail = await uploadRes.text().catch(() => '')
    throw new Error('Storage upload failed (' + uploadRes.status + ')' + (detail ? ': ' + detail.slice(0, 200) : ''))
  }

  return publicUrl
}

/**
 * Upload a PDF to a Sheet Revision record (Supabase Storage).
 * 1. Gets a signed upload URL from /api/upload (authenticated with Clerk JWT).
 * 2. PUTs the file directly to Supabase Storage.
 * 3. PATCHes the revision record's PDF attachment field with the public URL.
 * @param {string} revisionId — Sheet Revision record ID
 * @param {File} file — the PDF File object
 * @returns {Promise<string>} The public URL of the uploaded PDF
 */
export async function uploadRevisionPdf(revisionId, file) {
  const publicUrl = await uploadPdfToStorage(file)

  await apiPatch(TBL_REV, revisionId, {
    PDF: [{ url: publicUrl, filename: file.name }],
  })

  return publicUrl
}

/**
 * Fetch all Drawing Sheet Templates, sorted by Sheet Template Sort Order.
 * Returns raw Airtable records with fields: Sheet Number, Sheet Name, Discipline.
 */
export function getSheetTemplates() {
  return apiGet(
    TBL_TPL,
    'fields%5B%5D=Sheet+Number&fields%5B%5D=Sheet+Name&fields%5B%5D=Discipline'
    + '&sort%5B0%5D%5Bfield%5D=Sheet+Template+Sort+Order&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=200'
  )
}

/**
 * Fetch Sheet Revision records linked to a milestone, sorted by sheet sort order.
 * Returns raw records; callers must deduplicate by drawing sheet ID before caching
 * (use getFirst(fields['Record ID (from Drawing Sheet)']) as the dedup key).
 * @param {string} msId — Milestone record ID
 * @returns {Promise<object[]>} Raw Airtable Sheet Revision records
 */
export function getMilestoneSheetRevisions(msId) {
  return apiGet(
    TBL_REV,
    'filterByFormula=' + encodeURIComponent('FIND("' + msId + '",ARRAYJOIN({Record ID (from Deliverables)},","))')
    + '&sort%5B0%5D%5Bfield%5D=Sheet+Sort+Order+(from+Drawing+Sheet)&sort%5B0%5D%5Bdirection%5D=asc'
    + '&maxRecords=100'
  )
}

/**
 * Fetch Sheet Revision records for multiple Drawing Sheets in a single batch pass.
 * Used to pre-populate ctx.sheetRevCache so PDF icons show without expansion.
 * Groups sheet IDs into batches of 20; returns all records combined.
 * Uses {Drawing Sheet} (multipleRecordLinks → returns actual IDs per Airtable API rules).
 * @param {string[]} sheetIds — Drawing Sheet record IDs
 * @returns {Promise<object[]>} All matching revision records
 */
export async function getProjectSheetRevisions(sheetIds) {
  if (!sheetIds || sheetIds.length === 0) return []
  const BATCH = 20
  const batches = []
  for (let i = 0; i < sheetIds.length; i += BATCH) batches.push(sheetIds.slice(i, i + BATCH))
  const results = await Promise.all(batches.map(batch => {
    const clauses = batch.map(id => 'FIND("' + id + '",ARRAYJOIN({Record ID (from Drawing Sheet)},","))').join(',')
    const formula = batch.length === 1 ? clauses : 'OR(' + clauses + ')'
    return apiGet(
      TBL_REV,
      'filterByFormula=' + encodeURIComponent(formula)
      + '&sort%5B0%5D%5Bfield%5D=Posted+Date+(from+Deliverables)&sort%5B0%5D%5Bdirection%5D=desc'
      + '&maxRecords=200'
    )
  }))
  return results.flat()
}

/**
 * All Drawing Sheets for a project, each with its latest revision's PDF URL attached —
 * candidates for "use an existing sheet as a background" pickers (e.g. the Program Model
 * tool). Sheets with no revision or no PDF are still returned (pdfUrl: null) so callers can
 * show/exclude them explicitly rather than have them silently vanish.
 *
 * Uses `Drawing Sheet` (multipleRecordLinks — reliable via the API) to key revisions back to
 * their sheet, NOT the `Record ID (from Drawing Sheet)` lookup (multipleLookupValues of a
 * RECORD_ID() formula — returns empty via the API per Airtable API Rules; fine inside a
 * filterByFormula string server-side, which is all getProjectSheetRevisions uses it for, but
 * not safe to read back client-side).
 * @param {string} projectId
 * @returns {Promise<{id, fields, latestRevisionId, pdfUrl}[]>}
 */
export async function getProjectSheetsWithLatestPdf(projectId) {
  const sheets = await getProjectSheets(projectId)
  if (!sheets.length) return []
  const revisions = await getProjectSheetRevisions(sheets.map(s => s.id))
  // Each batch inside getProjectSheetRevisions is already sorted desc by Posted Date, and a
  // given sheet's revisions never span batches — so first-occurrence-per-sheetId is its latest.
  const latestBySheet = {}
  for (const rev of revisions) {
    const sheetId = (rev.fields['Drawing Sheet'] || [])[0]
    if (sheetId && !latestBySheet[sheetId]) latestBySheet[sheetId] = rev
  }
  return sheets.map(s => {
    const rev = latestBySheet[s.id] || null
    const pdf = rev?.fields?.PDF
    return {
      id: s.id,
      fields: s.fields,
      latestRevisionId: rev?.id || null,
      pdfUrl: Array.isArray(pdf) && pdf.length ? (pdf[0]?.url || null) : null,
    }
  })
}

/**
 * Find a real, already-uploaded PDF for a Drawing Sheet Template — so the titleblock/note-sticker
 * calibration panel (SheetTemplateTitleblockPanel) always calibrates against an actual sheet of
 * that exact type, across any project, instead of asking the user to hunt one down manually.
 *
 * Matches Drawing Sheets by the template's primary-field text (same trick analyzePushDrawing uses
 * in services/templates.js) since `{Drawing Sheet Template}` is a single link field and Airtable
 * formulas can compare a link field directly to the linked record's primary-field string.
 * @param {string} templateName — e.g. "A001 — Site Plan" (normalizeDsTemplate's `.name`)
 * @returns {Promise<{ pdfUrl: string, sheetId: string, revisionId: string } | null>}
 */
export async function getSampleSheetForTemplate(templateName) {
  if (!templateName) return null
  const nameEsc = templateName.replace(/"/g, '\\"')
  const formula = `{Drawing Sheet Template}="${nameEsc}"`
  const sheets = await apiGet(TBL_SHEET, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=50')
  if (!sheets.length) return null
  const revisions = await getProjectSheetRevisions(sheets.map(s => s.id))
  for (const rev of revisions) {
    const pdf = rev.fields?.PDF
    if (Array.isArray(pdf) && pdf.length && pdf[0]?.url) {
      const sheetId = (rev.fields['Drawing Sheet'] || [])[0]
      return { pdfUrl: pdf[0].url, sheetId, revisionId: rev.id }
    }
  }
  return null
}

/**
 * Fetch Slack Thread Timestamp for a set of Drawing Sheet record IDs.
 * Used by MilestoneSections to populate the thread indicator on sheets
 * viewed within a milestone — revision records don't carry the sheet's timestamp,
 * so a separate targeted fetch is needed.
 * @param {string[]} sheetIds — Drawing Sheet record IDs
 * @returns {Promise<object[]>} Airtable records [{id, fields: {'Slack Thread Timestamp': string}}]
 */
export function getSheetSlackTimestamps(sheetIds) {
  if (!sheetIds || sheetIds.length === 0) return Promise.resolve([])
  const clauses = sheetIds.map(id => 'RECORD_ID()="' + id + '"').join(',')
  const formula = sheetIds.length === 1 ? clauses : 'OR(' + clauses + ')'
  return apiGet(
    TBL_SHEET,
    'filterByFormula=' + encodeURIComponent(formula)
    + '&fields%5B%5D=Slack+Thread+Timestamp&maxRecords=100'
  )
}

/**
 * Clear the PDF attachment on a Sheet Revision record.
 * @param {string} revisionId
 */
export function removeRevisionPdf(revisionId) {
  return apiPatch(TBL_REV, revisionId, { PDF: [] })
}

/**
 * Move a Sheet Revision to a different milestone by re-linking its Deliverables field.
 * The sheet disappears from the old milestone's branch and appears under the new one.
 * @param {string} revisionId — Sheet Revision record ID
 * @param {string} newMilestoneId — Target milestone (Project Deliverable) record ID
 */
export async function moveSheetRevisionToMilestone(revisionId, newMilestoneId) {
  const rev = await apiGetOneStrict(TBL_REV, revisionId)
  const sheetId = (rev?.fields?.['Drawing Sheet'] || [])[0]
  if (sheetId) await assertSheetNotOnMilestone(sheetId, newMilestoneId, revisionId)
  return apiPatch(TBL_REV, revisionId, { 'Deliverables': [newMilestoneId] })
}

/**
 * A sheet appears on a milestone once. A second revision of the same sheet on one milestone shows
 * as a duplicate row everywhere (outline, AlderQC's sheet list) and splits QC between two copies —
 * Yuzef's EXT Drawings got five of these in May. Throws with a readable message when the sheet
 * already has a revision on the milestone (other than `ignoreRevisionId`).
 */
async function assertSheetNotOnMilestone(sheetId, milestoneId, ignoreRevisionId = null) {
  const onMs = await getMilestoneSheetRevisions(milestoneId)
  const dup = onMs.find(r => r.id !== ignoreRevisionId && (r.fields?.['Drawing Sheet'] || []).includes(sheetId))
  if (dup) {
    const name = (dup.fields?.['Sheet Number and Title (from Drawing Sheet)'] || [])[0] || 'This sheet'
    throw new Error(name + ' is already on this milestone.')
  }
}

/**
 * Delete a single Sheet Revision record.
 * Caller is responsible for checking that no newer revisions exist before calling this.
 * @param {string} revisionId — Sheet Revision record ID
 */
export function deleteSheetRevision(revisionId) {
  return apiDelete(TBL_REV, revisionId)
}

/**
 * Delete a Drawing Sheet record entirely (sheet + all its metadata).
 * Only called when deleting the sole "Original Release" revision — removes the sheet
 * from the project completely, not just from one milestone.
 * @param {string} sheetId — Drawing Sheet record ID
 */
export function deleteDrawingSheet(sheetId) {
  return apiDelete(TBL_SHEET, sheetId)
}

/**
 * Update editable fields on a Sheet Revision record.
 * @param {string} revisionId
 * @param {{ summary?: string, type?: string }} fields
 */
export function updateRevision(revisionId, { summary, type } = {}) {
  const fields = {}
  if (summary !== undefined) fields['Summary of Change'] = summary
  if (type    !== undefined) fields['Type'] = type
  return apiPatch(TBL_REV, revisionId, fields, { typecast: true })
}

/**
 * Create Drawing Sheet records for a project in batches of 10.
 * @param {string} projectId
 * @param {{ sheetNumber: string, sheetTitle: string, templateId: string|null }[]} sheets
 * @returns {Promise<string[]>} Array of created sheet record IDs
 */
export async function createSheets(projectId, sheets) {
  const records = sheets.map(s => ({
    'Sheet Number': s.sheetNumber,
    'Sheet Title':  s.sheetTitle,
    'Project':      [projectId],
    ...(s.templateId ? { 'Drawing Sheet Template': [s.templateId] } : {}),
  }))
  const created = await apiPostBatch(TBL_SHEET, records)
  return created.map(r => r.id)
}

/**
 * Create Sheet Revision records linking sheets to a milestone.
 * All revisions are created as 'Original Release'.
 * @param {string[]} sheetIds
 * @param {string} milestoneId
 */
export function createSheetRevisions(sheetIds, milestoneId) {
  const records = sheetIds.map(sheetId => ({
    'Drawing Sheet':    [sheetId],
    'Type':             'Original Release',
    'Deliverables':     [milestoneId],
    'Summary of Change':'Initial release — sheet first issued in the drawing set.',
  }))
  return apiPostBatch(TBL_REV, records)
}

/**
 * Add brand-new Drawing Sheets to a milestone — THE one way a sheet enters Cortex (the outline's
 * Add Sheets wizard and an AlderCAD draft sheet's "Add to a milestone" both call it): the Drawing
 * Sheet records, their Original Release revisions on that milestone, and each sheet's checklist
 * from its Drawing Sheet Template (nothing else creates it, and the checklist is what gates a
 * drawing going onto a milestone). A checklist failure doesn't unwind sheets already created.
 * @param {string} projectId
 * @param {string} milestoneId
 * @param {{ sheetNumber: string, sheetTitle: string, templateId: string|null }[]} sheets
 * @returns {Promise<string[]>} the new Drawing Sheet record IDs, in order
 */
export async function createNewSheetsInMilestone(projectId, milestoneId, sheets) {
  if (!sheets.length) return []
  const sheetIds = await createSheets(projectId, sheets)
  await createSheetRevisions(sheetIds, milestoneId)
  await Promise.all(sheetIds.map(id => resetSheetChecklist(id)))
    .catch(err => console.warn('[sheets] checklist seeding error:', err))
  // Reference/Production > Drawing Sheets only refetches a sheet's revisions on this event
  window.dispatchEvent(new CustomEvent('sheet-revisions-stale', { detail: { sheetIds, msId: milestoneId } }))
  return sheetIds
}

/**
 * Update QC state fields on a Sheet Revision record.
 * Fields live on Sheet Revisions (tblBfKyVWRAlW3tmj) — one revision per milestone,
 * so QC state is automatically milestone-scoped.
 *
 * @param {string} revisionId — Sheet Revision record ID
 * @param {object} fields — e.g. { 'Ready for QC': true } or { 'QC Approved': true, 'Ready for QC': false }
 * @returns {Promise<object>} Updated revision record
 */
export function updateSheetRevisionQC(revisionId, fields) {
  return apiPatch(TBL_REV, revisionId, fields)
}

/**
 * Current drawing set (latest POSTED revision per project sheet, viewer-ready) — powers the
 * "View drawing set" action in Reference > Production > Drawing Sheets: the same flip-through
 * viewer the contractor portal uses. Clerk-authed (api/drawing-set.js), same resolution/priority
 * chain as the public contractor portal (api/_drawingSet.js, shared by both).
 * @param {string} projectId
 * @returns {Promise<Array>} normalized drawing objects ({ id, pdfUrl, number, title, discipline, versions })
 */
export async function getCurrentDrawingSet(projectId) {
  const token = typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null
  const res = await fetch('/api/drawing-set', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ projectId }),
  })
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not load the drawing set')
  const { drawings } = await res.json()
  return drawings || []
}

/**
 * SET SCAFFOLDING (AlderCAD round 8): the newest uploaded PDF on a Drawing Sheet — fetched fresh,
 * because an Airtable attachment link expires within hours and a sheet that places the survey
 * must keep finding it. Null when no revision carries a PDF.
 * @param {string} sheetId — Drawing Sheet record ID
 */
export async function getLatestSheetPdfUrl(sheetId) {
  if (!sheetId) return null
  const revs = await getSheetRevisions(sheetId).catch(() => [])
  const hit = revs.find((r) => r.fields?.PDF?.length && r.fields.PDF[0]?.url)
  return hit ? hit.fields.PDF[0].url : null
}
